import type { Feeder, StepForms } from '@/stores/slices/setup-fund-slice';

import { SUB_STEP_ORDER } from '../sub-step-order';

const DEFAULT_IGNORED_KEYS = new Set(['id', 'uid', 'createdAt', 'updatedAt']);

function normalize(value: unknown): unknown {
  if (value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (Array.isArray(value)) {
    return value.map(normalize);
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    Object.keys(obj)
      .sort()
      .forEach((k) => {
        if (DEFAULT_IGNORED_KEYS.has(k)) return;
        out[k] = normalize(obj[k]);
      });
    return out;
  }
  return value;
}

function isPrimitive(v: unknown) {
  return v == null || typeof v !== 'object';
}

function collectDescendantPaths(valueRaw: unknown, basePath: string): string[] {
  const value = normalize(valueRaw);

  if (isPrimitive(value)) {
    return basePath ? [basePath] : [];
  }

  if (Array.isArray(value)) {
    const out: string[] = [];
    value.forEach((item, i) => {
      const p = basePath ? `${basePath}[${i}]` : `[${i}]`;
      out.push(...collectDescendantPaths(item, p));
    });
    return out;
  }

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const out: string[] = [];
  keys.forEach((k) => {
    const nextPath = basePath ? `${basePath}.${k}` : k;
    out.push(...collectDescendantPaths(obj[k], nextPath));
  });
  return out;
}

export function diffPaths(aRaw: unknown, bRaw: unknown, basePath = ''): string[] {
  const a = normalize(aRaw);
  const b = normalize(bRaw);

  if (isPrimitive(a) || isPrimitive(b)) {
    if (a === b) return [];

    if (!isPrimitive(a) && isPrimitive(b)) {
      return collectDescendantPaths(a, basePath);
    }
    if (isPrimitive(a) && !isPrimitive(b)) {
      return collectDescendantPaths(b, basePath);
    }

    return basePath ? [basePath] : [''];
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    const max = Math.max(aa.length, bb.length);
    const out: string[] = [];
    if (aa.length !== bb.length && basePath) out.push(basePath);
    for (let i = 0; i < max; i += 1) {
      const p = `${basePath}[${i}]`;
      out.push(...diffPaths(aa[i], bb[i], p));
    }
    return out;
  }

  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keys = Array.from(new Set([...Object.keys(ao), ...Object.keys(bo)])).sort();
  const out: string[] = [];

  keys.forEach((k) => {
    const nextPath = basePath ? `${basePath}.${k}` : k;
    out.push(...diffPaths(ao[k], bo[k], nextPath));
  });

  return out;
}

const STEP_PREFIXES: Record<string, string[]> = Object.fromEntries(
  SUB_STEP_ORDER.map((s) => [s.code, s.diffPrefixes]),
);

export function countStepChanges(step: string, changedPaths: string[]) {
  const prefixes = STEP_PREFIXES[step] ?? [];
  if (!prefixes.length) return 0;
  const set = new Set<string>();
  changedPaths.forEach((p) => {
    if (!p) return;
    if (
      prefixes.some((pref) => p === pref || p.startsWith(`${pref}.`) || p.startsWith(`${pref}[`))
    ) {
      set.add(p);
    }
  });
  return set.size;
}

export function isPathChanged(changedPaths: string[], path: string) {
  return changedPaths.includes(path);
}

function pairFeedersById(approved: Feeder[], draft: Feeder[]): (Feeder | undefined)[] {
  const approvedById = new Map(
    approved.filter((feeder) => feeder.id != null).map((feeder) => [feeder.id, feeder]),
  );
  if (!draft.some((feeder) => feeder.id != null && approvedById.has(feeder.id))) return approved;

  const paired = draft.map((feeder) =>
    feeder.id != null ? approvedById.get(feeder.id) : undefined,
  );
  const pairedSet = new Set(paired);
  return [...paired, ...approved.filter((feeder) => !pairedSet.has(feeder))];
}

export function alignApprovedFeeders(approved: StepForms, draft: StepForms): StepForms {
  const feeders = pairFeedersById(approved.feeders ?? [], draft.feeders ?? []).filter(
    (feeder): feeder is Feeder => feeder != null,
  );
  return { ...approved, feeders };
}

export function computeDraftDiff(approved: StepForms, draft: StepForms) {
  const changed = diffPaths(
    { ...approved, feeders: pairFeedersById(approved.feeders ?? [], draft.feeders ?? []) },
    draft,
  );
  if ((approved.feeders ?? []).length !== (draft.feeders ?? []).length) changed.push('feeders');
  const changedPaths = Array.from(new Set(changed.filter(Boolean)));
  const perStep: Record<string, number> = {};
  Object.keys(STEP_PREFIXES).forEach((s) => {
    perStep[s] = countStepChanges(s, changedPaths);
  });
  return { changedPaths, perStep };
}
